import { type IDataObject, queryBuilder } from '@inpart-io/devtools-iap';
import { GET_BY_ID_LIST, RETRIEVE_BY_QUERY } from '@inpart-io/devtools-vue';
import type { IInfoGroup, IInfoGroupField } from '@inpart-io/padme-iap';
import { FieldValueType, privateFieldCustomListValueProperties } from '@inpart-io/padme-iap';
import { reactive } from 'vue';

import { BooleanKeys, type IFilter } from '@/components/filters/constants/filters.constants';
import { filtersService } from '@/components/filters/services/filters-service';
import { useInfoGroupClassName } from '@/composables/infogroupClassNameHelper';
import { criteriaProps } from '@/pages/platform-settings/constants/platform-settings.constants';
import { router } from '@/router';
import { getInfogroupCriteria, isInfogroupFieldEnabled } from '@/services/infogroups-service';
import { store } from '@/store/index';
import { PRIVATE_FIELD_CUSTOM_VALUE_MODULE } from '@/store/private-field-custom-value-module';

export function useInfoGroupFiltersHelper(): {
    infoGroupDateFilterData: Map<string, string | null>;
    infoGroupNumberFilterData: Map<string, number | null>;
    infoGroupBooleanFilterData: Map<string, boolean | null>;
    infoGroupListValueFilterData: Map<string, IDataObject[] | null>;
    getLessThanNumberTypeFilters: (infoGroups: IInfoGroup[]) => IFilter[];
    getGreaterThanNumberTypeFilters: (infoGroups: IInfoGroup[]) => IFilter[];
    getAfterOrEqualDateTypeFilters: (infoGroups: IInfoGroup[]) => IFilter[];
    getBeforeOrEqualDateTypeFilters: (infoGroups: IInfoGroup[]) => IFilter[];
    getListValueTypeFilters: (infoGroups: IInfoGroup[]) => IFilter[];
    getBooleanTypeFilters: (infoGroups: IInfoGroup[]) => IFilter[];
    getTextTypeFilters: (infoGroups: IInfoGroup[]) => IFilter[];
    clearInfoGroupFilters: () => void;
    loadInfoGroupsFilterData: (allInfoGroups: IInfoGroup[]) => void;
} {
    const { getInfoGroupAttributeContainer } = useInfoGroupClassName();

    const infoGroupDateFilterData = reactive(new Map<string, string | null>());
    const infoGroupNumberFilterData = reactive(new Map<string, number | null>());
    const infoGroupBooleanFilterData = reactive(new Map<string, boolean | null>());
    const infoGroupListValueFilterData = reactive(new Map<string, IDataObject[] | null>());

    const getInfogroupCriteriaValue = (infogroup: IInfoGroup): string[] => {
        if (infogroup?.forClassName && criteriaProps.has(infogroup.forClassName)) {
            const configuration = criteriaProps.get(infogroup.forClassName);
            return getInfogroupCriteria(infogroup)?.[configuration.criteriaName];
        }
        return [];
    };

    const getLessThanNumberTypeFilters = (infoGroups: IInfoGroup[]): IFilter[] => {
        const numberTypeFilters: IFilter[] = [];
        infoGroups.forEach((infoGroup: IInfoGroup) => {
            infoGroup.fields.forEach((field: IInfoGroupField) => {
                if (field.valueType === FieldValueType.NUMBER && isInfogroupFieldEnabled(field)) {
                    numberTypeFilters.push({
                        filter: `IG${field.infogroupFieldType}Max`,
                        attribute: `${getInfoGroupAttributeContainer(infoGroup)}.${infoGroup.attributeName}.${field.attributeName}`,
                        withCriteriaName: criteriaProps.get(infoGroup.forClassName)?.criteriaName,
                        withCriteriaValue: getInfogroupCriteriaValue(infoGroup)
                    });
                }
            });
        });
        return numberTypeFilters;
    };

    const getGreaterThanNumberTypeFilters = (infoGroups: IInfoGroup[]): IFilter[] => {
        const numberTypeFilters: IFilter[] = [];
        infoGroups.forEach((infoGroup: IInfoGroup) => {
            infoGroup.fields.forEach((field: IInfoGroupField) => {
                if (field.valueType === FieldValueType.NUMBER && isInfogroupFieldEnabled(field)) {
                    numberTypeFilters.push({
                        filter: `IG${field.infogroupFieldType}Min`,
                        attribute: `${getInfoGroupAttributeContainer(infoGroup)}.${infoGroup.attributeName}.${field.attributeName}`,
                        withCriteriaName: criteriaProps.get(infoGroup.forClassName)?.criteriaName,
                        withCriteriaValue: getInfogroupCriteriaValue(infoGroup)
                    });
                }
            });
        });
        return numberTypeFilters;
    };

    const getAfterOrEqualDateTypeFilters = (infoGroups: IInfoGroup[]): IFilter[] => {
        const dateTypeFilters: IFilter[] = [];
        infoGroups.forEach((infoGroup: IInfoGroup) => {
            infoGroup.fields.forEach((field: IInfoGroupField) => {
                if (field.valueType === FieldValueType.DATE && isInfogroupFieldEnabled(field)) {
                    dateTypeFilters.push({
                        filter: `IG${field.infogroupFieldType}Start`,
                        attribute: `${getInfoGroupAttributeContainer(infoGroup)}.${infoGroup.attributeName}.${field.attributeName}`,
                        withCriteriaName: criteriaProps.get(infoGroup.forClassName)?.criteriaName,
                        withCriteriaValue: getInfogroupCriteriaValue(infoGroup)
                    });
                }
            });
        });
        return dateTypeFilters;
    };

    const getBeforeOrEqualDateTypeFilters = (infoGroups: IInfoGroup[]): IFilter[] => {
        const dateTypeFilters: IFilter[] = [];
        infoGroups.forEach((infoGroup: IInfoGroup) => {
            infoGroup.fields.forEach((field: IInfoGroupField) => {
                if (field.valueType === FieldValueType.DATE && isInfogroupFieldEnabled(field)) {
                    dateTypeFilters.push({
                        filter: `IG${field.infogroupFieldType}End`,
                        attribute: `${getInfoGroupAttributeContainer(infoGroup)}.${infoGroup.attributeName}.${field.attributeName}`,
                        withCriteriaName: criteriaProps.get(infoGroup.forClassName)?.criteriaName,
                        withCriteriaValue: getInfogroupCriteriaValue(infoGroup)
                    });
                }
            });
        });
        return dateTypeFilters;
    };

    const getListValueTypeFilters = (infoGroups: IInfoGroup[]): IFilter[] => {
        const listValueTypeFilters: IFilter[] = [];
        infoGroups.forEach((infoGroup: IInfoGroup) => {
            infoGroup.fields.forEach((field: IInfoGroupField) => {
                if (field.valueType === FieldValueType.LIST_VALUE && isInfogroupFieldEnabled(field)) {
                    listValueTypeFilters.push({
                        filter: `IG${field.infogroupFieldType}`,
                        attribute: `${getInfoGroupAttributeContainer(infoGroup)}.${infoGroup.attributeName}.${field.attributeName}`,
                        withCriteriaName: criteriaProps.get(infoGroup.forClassName)?.criteriaName,
                        withCriteriaValue: getInfogroupCriteriaValue(infoGroup)
                    });
                }
            });
        });
        return listValueTypeFilters;
    };

    const getBooleanTypeFilters = (infoGroups: IInfoGroup[]): IFilter[] => {
        const booleanTypeFilters: IFilter[] = [];
        infoGroups.forEach((infoGroup: IInfoGroup) => {
            infoGroup.fields.forEach((field: IInfoGroupField) => {
                if (field.valueType === FieldValueType.BOOLEAN && isInfogroupFieldEnabled(field)) {
                    booleanTypeFilters.push({
                        filter: `IG${field.infogroupFieldType}`,
                        attribute: `${getInfoGroupAttributeContainer(infoGroup)}.${infoGroup.attributeName}.${field.attributeName}`,
                        withCriteriaName: criteriaProps.get(infoGroup.forClassName)?.criteriaName,
                        withCriteriaValue: getInfogroupCriteriaValue(infoGroup)
                    });
                }
            });
        });
        return booleanTypeFilters;
    };

    const getTextTypeFilters = (infoGroups: IInfoGroup[]): IFilter[] => {
        const textTypeFilters: IFilter[] = [];
        infoGroups.forEach((infoGroup: IInfoGroup) => {
            infoGroup.fields.forEach((field: IInfoGroupField) => {
                if ((field.valueType === FieldValueType.TEXT || field.valueType === FieldValueType.LONG_TEXT) && isInfogroupFieldEnabled(field)) {
                    textTypeFilters.push({
                        filter: field.attributeName,
                        attribute: `${getInfoGroupAttributeContainer(infoGroup)}.${infoGroup.attributeName}.${field.attributeName}`
                    });
                }
            });
        });
        return textTypeFilters;
    };

    const clearInfoGroupFilters = (): void => {
        infoGroupBooleanFilterData.clear();
        infoGroupDateFilterData.clear();
        infoGroupListValueFilterData.clear();
        infoGroupNumberFilterData.clear();
    };

    const loadInfoGroupsFilterData = (allInfoGroups: IInfoGroup[]): void => {
        const booleanFilters = getBooleanTypeFilters(allInfoGroups).map((booleanFilter: IFilter) => {
            return booleanFilter.filter;
        });
        booleanFilters.forEach((filter: string) => {
            const getFilterFromUrl = filtersService.parseStringList(router.currentRoute.value.query, filter)[0]?.toUpperCase() ?? '';
            if (getFilterFromUrl === BooleanKeys.TRUE) {
                infoGroupBooleanFilterData.set(filter, true);
            } else if (getFilterFromUrl === BooleanKeys.FALSE) {
                infoGroupBooleanFilterData.set(filter, false);
            } else {
                infoGroupBooleanFilterData.delete(filter);
            }
        });
        const dateFilters = [...getBeforeOrEqualDateTypeFilters(allInfoGroups), ...getAfterOrEqualDateTypeFilters(allInfoGroups)].map((dateFilter: IFilter) => {
            return dateFilter.filter;
        });
        dateFilters.forEach((filter: string) => {
            const dateValue = (router.currentRoute.value.query[filter] as string) ?? null;
            const currentValue = infoGroupDateFilterData.get(filter);
            if (dateValue) {
                infoGroupDateFilterData.set(filter, dateValue);
            } else if (currentValue) {
                infoGroupDateFilterData.delete(filter);
            }
        });
        const numberFilters = [...getGreaterThanNumberTypeFilters(allInfoGroups), ...getLessThanNumberTypeFilters(allInfoGroups)].map((dateFilter: IFilter) => {
            return dateFilter.filter;
        });
        numberFilters.forEach((filter: string) => {
            const numberValue = filtersService.parseNumberList(router.currentRoute.value.query, filter)[0];
            const currentValue = infoGroupNumberFilterData.get(filter);
            if (typeof numberValue === 'number') {
                infoGroupNumberFilterData.set(filter, numberValue);
            } else if (currentValue) {
                infoGroupNumberFilterData.delete(filter);
            }
        });
        const listValueFilters = getListValueTypeFilters(allInfoGroups).map((listValueFilter: IFilter) => {
            return listValueFilter.filter;
        });
        listValueFilters.forEach((filter: string) => {
            const idList = filtersService.parseIdList(router.currentRoute.value.query, filter);
            const currentFilter = infoGroupListValueFilterData.get(filter);
            if (idList?.length > 0) {
                void store
                    .dispatch(`${PRIVATE_FIELD_CUSTOM_VALUE_MODULE}/${RETRIEVE_BY_QUERY}`, {
                        query: queryBuilder.build(queryBuilder.op.and(queryBuilder.cmp.isIn('dobjId', idList))),
                        properties: privateFieldCustomListValueProperties.DEFAULT
                    })
                    .then((response: number[]) => {
                        const values = store.getters[`${PRIVATE_FIELD_CUSTOM_VALUE_MODULE}/${GET_BY_ID_LIST}`](response);
                        infoGroupListValueFilterData.set(filter, values);
                    });
            } else if (currentFilter) {
                infoGroupListValueFilterData.delete(filter);
            }
        });
    };

    return {
        getLessThanNumberTypeFilters,
        getGreaterThanNumberTypeFilters,
        getAfterOrEqualDateTypeFilters,
        getBeforeOrEqualDateTypeFilters,
        getListValueTypeFilters,
        getBooleanTypeFilters,
        getTextTypeFilters,
        clearInfoGroupFilters,
        loadInfoGroupsFilterData,
        infoGroupDateFilterData,
        infoGroupNumberFilterData,
        infoGroupBooleanFilterData,
        infoGroupListValueFilterData
    };
}
